/**
 * P2.5: MfaVerifyPage
 *
 * TOTP code entry page shown after password login when the doctor has MFA enabled.
 * The login endpoint returns { mfaRequired: true, mfaToken } instead of the normal
 * token. This page consumes the mfaToken and exchanges it for a full access token.
 *
 * Route: /mfa/verify
 * State passed via location.state: { mfaToken: string, hospitalSlug: string }
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface LocationState {
  mfaToken?: string;
  hospitalSlug?: string;
}

interface VerifyLoginResponse {
  accessToken: string;
  user: {
    id: string;
    name: string;
    email: string;
    hospitalId: string;
    hospitalSlug: string;
    primaryLanguageCode: string;
    specializationId: string;
    role: string;
  };
}

export function MfaVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { acceptMfaToken } = useAuth();

  const state = (location.state as LocationState) ?? {};
  const mfaToken = state.mfaToken;

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useBackup, setUseBackup] = useState(false);

  // If no mfaToken in state, redirect to login
  if (!mfaToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
        <div className="clinical-card p-8 text-center max-w-sm w-full">
          <p className="font-body text-clinical-text-muted">Session expired. Please log in again.</p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="clinical-button-primary mt-4 w-full"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting || !code.trim()) return;
    setError(null);
    setIsSubmitting(true);

    const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

    try {
      const httpRes = await fetch(`${BASE}/api/auth/mfa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mfaToken, code: code.trim() }),
      });
      if (!httpRes.ok) throw new Error('Invalid code');
      const res = await httpRes.json() as VerifyLoginResponse;

      // Hand off full access token to AuthContext
      acceptMfaToken(res.accessToken, { ...res.user, role: 'doctor' });
      navigate('/dashboard', { replace: true });
    } catch {
      setError('Invalid or expired code. Please try again.');
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="clinical-card p-8 w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="w-12 h-12 rounded-full bg-clinical-cream-dark flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-clinical-charcoal" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
          </div>
          <h1 className="font-heading text-xl text-clinical-charcoal">Two-Factor Authentication</h1>
          <p className="font-body text-sm text-clinical-text-muted mt-1">
            {useBackup ? 'Enter a backup code' : 'Enter the 6-digit code from your authenticator app'}
          </p>
        </div>

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <input
              type={useBackup ? 'text' : 'text'}
              inputMode={useBackup ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              maxLength={useBackup ? 16 : 6}
              value={code}
              onChange={(e) => {
                const val = useBackup ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '');
                setCode(val);
              }}
              className="clinical-input w-full text-center text-xl tracking-widest"
              placeholder={useBackup ? 'XXXXXXXXXXXXXXXX' : '000000'}
              autoFocus
              required
            />
          </div>

          {error && (
            <p className="font-body text-sm text-red-600 text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || code.length < (useBackup ? 8 : 6)}
            className="clinical-button-primary w-full"
          >
            {isSubmitting ? 'Verifying...' : 'Verify'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => { setUseBackup(!useBackup); setCode(''); setError(null); }}
            className="font-body text-xs text-clinical-amber hover:underline"
          >
            {useBackup ? 'Use authenticator app instead' : 'Use a backup code instead'}
          </button>
        </div>
      </div>
    </div>
  );
}
